import React, { useState, useEffect } from "react";
import {
  Select,
  Checkbox,
  Button,
  message,
  Spin,
  Modal,
  Input,
  DatePicker,
} from "antd";
import API from "../../config/api";
import * as XLSX from "xlsx";
import "./styles.css";
import type { RangePickerProps } from "antd/es/date-picker";
import dayjs from "dayjs";
import { DownloadOutlined } from "@ant-design/icons";
import type { DatePickerProps } from "antd";
import { GET } from "../../utils/apiCalls";
import { useSelector } from "react-redux";

const { RangePicker } = DatePicker;

interface Branch {
  id: string;
  name: string;
}

interface Product {
  id: number;
  name: string;
  quantity: number;
  date: string;
  sp_price?: number; // Add unit price
  selected?: boolean;
}

interface ProductDetail {
  id: number;
  name: string;
  quantity: number;
  date: string;
  idescription?: string;
  sp_price?: number;
  itemtype?: string;
}

interface DateFilter {
  key: string;
  from_date: string;
  to_date: string;
}

const ProductWiseReport: React.FC = () => {
  const { user } = useSelector((state: any) => state.User);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<any>(
    user?.staff?.companyid || null
  );
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true); // Initial loading for branches and products
  const [loadingProducts, setLoadingProducts] = useState(false); // Loading for product specific actions
  const [selectedProduct, setSelectedProduct] = useState<ProductDetail | null>(
    null
  );
  const [modalVisible, setModalVisible] = useState(false);
  const [page, setPage] = useState(1);
  const [take] = useState(10);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState("");
  const [date, setDate] = useState<DateFilter>({
    key: "Today",
    from_date: dayjs().startOf("day").format("YYYY-MM-DD HH:mm:ss"),
    to_date: dayjs().endOf("day").format("YYYY-MM-DD HH:mm:ss"),
  });
  const [error, setError] = useState<string | null>(null);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    if (selectedBranch) {
      fetchProducts();
    }
  }, [selectedBranch, page, query, date]);

  const fetchBranches = async () => {
    try {
      setLoading(true);
      const response: any = await GET(API.BRANCHES_LIST, {});
      if (response.data && Array.isArray(response.data)) {
        const formattedBranches = response.data.map((item: any) => ({
          id: item.CompanyMaster.id,
          name: item.CompanyMaster.bname,
        }));
        setBranches(formattedBranches);
        if (formattedBranches.length > 0) {
          setSelectedBranch(formattedBranches[0]); // Select first branch by default
        }
      } else {
        message.error("Invalid branch data received");
      }
    } catch (error) {
      message.error("Failed to fetch branches");
      setError("Failed to load branches");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    if (!selectedBranch) return;

    setLoadingProducts(true);
    try {
      setError(null);
      const branchId = selectedBranch.id || user?.staff?.companyid;
      let filter1 = `?page=${page}`;
      let filter2 = `&limit=${take}`;
      let filter3 = query ? `&query=${query}` : "";
      let filter4 = date?.key
        ? `&from_date=${date?.from_date}&to_date=${date?.to_date}`
        : "";
      let filter5 = `&branchId=${branchId}`;

      const response: any = await GET(
        `report_app/products${filter1}${filter2}${filter3}${filter4}${filter5}`,
        {}
      );

      if (response.data && Array.isArray(response.data)) {
        const mappedProducts = response.data.map((product: any) => {
          return {
            id: product.productId,
            name: product.productMaster?.idescription,
            quantity: parseInt(product.totalSold),
            date: dayjs(product.date).format("YYYY-MM-DD") || "N/A",
            sp_price: parseFloat(product.productMaster?.sp_price) || 0,
            selected: false,
          };
        });
        setProducts(mappedProducts);
        setTotal(response.data.length);
      } else if (response.data && response.data.items) {
        const mappedProducts = response.data.items.map((product: any) => {
          return {
            id: product.productId,
            name: product.productMaster?.idescription,
            quantity: parseInt(product.totalSold),
            date: dayjs(product.date).format("YYYY-MM-DD") || "N/A",
            sp_price: parseFloat(product.productMaster?.sp_price) || 0,
            selected: false,
          };
        });
        setProducts(mappedProducts);
        setTotal(response.data.total || response.data.items.length);
      } else {
        setProducts([]);
        setTotal(0);
      }
    } catch (error) {
      setError("Failed to fetch products");
      message.error("Failed to fetch products");
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchProductDetails = async (productId: number) => {
    try {
      const response: any = await GET(
        `report_app/v2/product_details/${productId}`,
        {}
      );
      if (response.data && response.data.product) {
        setSelectedProduct({
          ...response.data.product,
          quantity: parseInt(response.data.product.totalSold) || 0, // Ensure quantity is parsed
          date: dayjs(response.data.product.date).format("YYYY-MM-DD") || "N/A",
          sp_price: parseFloat(response.data.product.sp_price) || 0, // Ensure sp_price is parsed
        });
        setModalVisible(true);
      } else {
        message.error("Product details not found or invalid response");
      }
    } catch (error) {
      message.error("Failed to fetch product details");
    }
  };

  const handleBranchChange = (value: string) => {
    const branch = branches.find((b) => b.id === value);
    setSelectedBranch(branch || null);
    setPage(1);
  };

  const handleSearch = (value: string) => {
    setQuery(value);
    setPage(1);
  };

  const dateFilters = [
    {
      key: "Today",
      from_date: dayjs().startOf("day").format("YYYY-MM-DD HH:mm:ss"),
      to_date: dayjs().endOf("day").format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      key: "Yesterday",
      from_date: dayjs()
        .subtract(1, "day")
        .startOf("day")
        .format("YYYY-MM-DD HH:mm:ss"),
      to_date: dayjs()
        .subtract(1, "day")
        .endOf("day")
        .format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      key: "This month",
      from_date: dayjs().startOf("month").format("YYYY-MM-DD HH:mm:ss"),
      to_date: dayjs().endOf("month").format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      key: "Last month",
      from_date: dayjs()
        .subtract(1, "month")
        .startOf("month")
        .format("YYYY-MM-DD HH:mm:ss"),
      to_date: dayjs()
        .subtract(1, "month")
        .endOf("month")
        .format("YYYY-MM-DD HH:mm:ss"),
    },
  ];

  const handleDateFilterChange = (filter: DateFilter) => {
    setDate(filter);
    setPage(1);
  };

  const handleCustomDateChange: DatePickerProps["onChange"] = (
    date,
    dateString
  ) => {
    if (date) {
      setDate({
        key: "Custom",
        from_date: date.startOf("day").format("YYYY-MM-DD HH:mm:ss"),
        to_date: date.endOf("day").format("YYYY-MM-DD HH:mm:ss"),
      });
    } else {
      setDate(dateFilters[0]); // Reset to Today if custom date cleared
    }
    setPage(1);
  };

  const handleProductSelect = (productId: number) => {
    setProducts(
      products.map((product) =>
        product.id === productId
          ? { ...product, selected: !product.selected }
          : product
      )
    );
  };

  const handleExportToExcel = () => {
    const selectedProducts = products.filter((product) => product.selected);

    if (selectedProducts.length === 0) {
      message.warning("Please select at least one product to export");
      return;
    }

    const dataToExport = selectedProducts.map((product) => ({
      "Product Name": product.name,
      "Unit Price": product.sp_price,
      Quantity: product.quantity,
      "Total Amount": (product.sp_price || 0) * (product.quantity || 0),
      Date: product.date,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Products");
    XLSX.writeFile(workbook, "product_report.xlsx");
  };

  // Group products by date
  const groupedProducts = products.reduce(
    (acc: { [key: string]: Product[] }, product) => {
      const dateKey = dayjs(product.date).format("YYYY-MM-DD");
      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(product);
      return acc;
    },
    {}
  );

  const sortedDates = Object.keys(groupedProducts).sort((a, b) =>
    dayjs(b).diff(dayjs(a))
  );

  const calculateTotalAmount = (product: Product) => {
    return (product.sp_price || 0) * (product.quantity || 0);
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    setProducts(products.map((product) => ({ ...product, selected: checked })));
  };

  return (
    <div className="product-report-container">
      <div className="product-report-header">
        {user?.staff?.staffAccess?.includes("administrator") ? (
          <div className="branch-select-container">
            <Select
              style={{ minWidth: 200, flexGrow: 1 }}
              placeholder="Select Branch"
              onChange={handleBranchChange}
              value={selectedBranch?.id}
              loading={loading}
            >
              {branches.map((branch) => (
                <Select.Option key={branch.id} value={branch.id}>
                  {branch.name}
                </Select.Option>
              ))}
            </Select>
          </div>
        ) : null}

        <div className="search-date-filter-container">
          <div
            className="select-all-container"
            style={{ display: "flex", alignItems: "center", marginRight: 16 }}
          >
            <Checkbox
              checked={selectAll}
              onChange={(e) => handleSelectAll(e.target.checked)}
              style={{ display: "flex", alignItems: "center" }}
            >
              <span style={{ marginLeft: 8 }}>Select All</span>
            </Checkbox>
          </div>
          <Input.Search
            placeholder="Search products..."
            onSearch={handleSearch}
            style={{ width: 200, marginRight: 8 }}
            loading={loadingProducts}
          />

          <DatePicker
            onChange={handleCustomDateChange}
            value={
              date?.key === "Custom" && date.from_date
                ? dayjs(date.from_date)
                : null
            }
          />

          <Button
            type="primary"
            onClick={handleExportToExcel}
            style={{ marginLeft: 8 }}
            icon={<DownloadOutlined />}
          >
            Export Selected
          </Button>
        </div>
      </div>

      <div className="date-filter-buttons">
        {dateFilters.map((filter) => (
          <Button
            key={filter.key}
            type={date?.key === filter.key ? "primary" : "default"}
            onClick={() => handleDateFilterChange(filter)}
            className="date-filter-button"
          >
            {filter.key}
          </Button>
        ))}
        <Button
          type={date?.key === "Custom" ? "primary" : "default"}
          onClick={() => {
            /* Handled by RangePicker directly */
          }}
          className="date-filter-button"
        >
          Custom
        </Button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loadingProducts ? (
        <div className="loading-container">
          <Spin size="large" />
        </div>
      ) : products.length === 0 ? (
        <div className="no-data-message">No products available</div>
      ) : (
        <div className="product-list-wrapper">
          {sortedDates.map((dateKey) => (
            <div key={dateKey} className="product-group">
              <div className="date-header">
                {groupedProducts[dateKey].length} products on{" "}
                {dayjs(dateKey).format("MMM D, YYYY")}
              </div>
              {groupedProducts[dateKey].map((product) => (
                <div
                  key={product.id}
                  className="product-item"
                  onClick={() => fetchProductDetails(product.id)}
                >
                  <Checkbox
                    checked={product.selected}
                    onChange={() => handleProductSelect(product.id)}
                    onClick={(e) => e.stopPropagation()} // Prevent row click when checkbox is clicked
                    className="product-checkbox"
                  />
                  <div className="product-info">
                    <div className="product-name-price">
                      <span className="product-name">{product.name}</span>
                      <span className="unit-price">
                        Unit price - {product.sp_price?.toFixed(2)}
                      </span>
                    </div>
                    <div className="product-amount-quantity">
                      <span className="total-amount">
                        {calculateTotalAmount(product).toFixed(2)}
                      </span>
                      <span className="units-sold">
                        {product.quantity} units sold
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
          {total > take && (
            <div className="pagination-controls">
              <Button
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <span>
                Page {page} of {Math.ceil(total / take)}
              </span>
              <Button
                onClick={() => setPage((prev) => prev + 1)}
                disabled={page * take >= total}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}

      <Modal
        title="Product Details"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        {selectedProduct && (
          <div className="product-details-modal">
            <p>
              <strong>Name:</strong> {selectedProduct.name}
            </p>
            <p>
              <strong>Description:</strong> {selectedProduct.idescription}
            </p>
            <p>
              <strong>Unit Price:</strong>{" "}
              {selectedProduct.sp_price?.toFixed(2)}
            </p>
            <p>
              <strong>Item Type:</strong> {selectedProduct.itemtype}
            </p>
            <p>
              <strong>Quantity:</strong> {selectedProduct.quantity}
            </p>
            <p>
              <strong>Date:</strong> {selectedProduct.date}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ProductWiseReport;
